import { Outlet, Navigate } from "react-router-dom";
import { useAuthProviderV2, useRoutesProvider } from "../providers/provider-hooks";
import PropTypes from "prop-types";
import { resolvePostAuthTarget } from "../domains/auth/utils/resolve-post-auth-target";
import { pasiInscriereActive } from "../domains/auth/utils/pasi-inscriere";
import { BandaInLucru } from "../shared/components/BandaInLucru";
import { LinkSetariCookie } from "../shared/components/LinkSetariCookie";

/**
 * UnauthenticatedLayout Component
 *
 * Layout wrapper for public (unauthenticated) routes. This component:
 * - Provides a layout for login, register, home, and other public pages
 * - Redirects already-authenticated users to their role-specific home page
 * - Minimal overhead - just checks auth status and renders Outlet
 *
 * React Router Pattern: Layout Route (no path)
 * Reference: https://github.com/remix-run/react-router/blob/main/docs/start/declarative/routing.md
 *
 * Usage:
 * ```jsx
 * <Route element={<UnauthenticatedLayout />}>
 *   <Route path="/" element={<Home />} />
 *   <Route path="/autentificare" element={<LogIn />} />
 *   <Route path="/inregistrare-profesor" element={<RegisterTeacher />} />
 * </Route>
 * ```
 *
 * Behavior:
 * - If user is NOT authenticated → Render child routes (login, register, etc.)
 * - If user IS authenticated → Redirect to their home page (dashboard)
 *
 * @returns {JSX.Element} Outlet for public routes or redirect for authenticated users
 */
export default function UnauthenticatedLayout() {
    const { userInfo, token } = useAuthProviderV2();
    const { getUserHomePage, PATHS, PAGES, ROLES } = useRoutesProvider();

    // Daca userul e autentificat, il trimitem unde ii e locul in fluxul lui, nu direct pe home:
    // un profesor cu cerere in asteptare trebuie sa ajunga pe pagina de asteptare, nu pe dashboard.
    // `pathname: null` — e pe o ruta publica, deci nu conteaza unde e acum.
    if (token && userInfo) {
        const target =
            resolvePostAuthTarget(userInfo, null, {
                PATHS,
                PAGES,
                ROLES,
                getUserHomePage,
                pasiInscriere: pasiInscriereActive(),
            }) ??
            getUserHomePage(userInfo.role);
        return <Navigate to={target} replace />;
    }

    // Render child routes (login, register, home, etc.). `<main>` e singurul reper de pagina
    // pe care il au azi cele 9 pagini publice (`/activitate/:token`, ruta care are deja
    // header+main proprii, foloseste layout-ul `open`, nu trece pe aici).
    return (
        <main>
            <Outlet />
            <LinkSetariCookie />
            <BandaInLucru />
        </main>
    );
}

UnauthenticatedLayout.propTypes = {};